/* ==========================================================================
   Predictive Search (SKU-ready)
   - Searches title + tags + product_type + variant SKUs + product SKU
   - Optionally boosts exact SKU matches to the top of the products list
   - Keeps your layout/behaviour from the original component
   ========================================================================== */

class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    // ---- internal state ---------------------------------------------------
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    // ---- layout references ------------------------------------------------
    this.body = document.querySelector("body");
    this.headerBottom = document.querySelector(".header__bottom");
    this.promoModal = document.querySelector(".search__modal");
    this.headerInput = document.querySelector(".header__search .search__input");

    this.calcHeight();
    this.setupEventListeners();

    // Watch viewport changes to recalc dropdown height cap
    const searchResizeObserver = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (entry.contentRect.width < 991) {
        this.calcHeight(65);
      } else {
        this.calcHeight(0);
      }
    });
    searchResizeObserver.observe(this.body);
  }

  /* ---------------------------- Layout helpers --------------------------- */
  calcHeight(buttonHeight) {
    const getH = (sel) =>
      document.querySelector(sel)
        ? document.querySelector(sel).getBoundingClientRect().height
        : 0;

    const heightAnnouncementBar = getH(".section-announcement");
    const heightHeader = getH(".shopify-section-header");
    const heightSearchTop = getH(".search-modal__top");
    const heightInput = this.headerInput ? this.headerInput.getBoundingClientRect().height : 0;

    if (document.querySelector(".predictive-search")) {
      const clientHeight = document.documentElement.clientHeight;
      const maxHeightAllowed =
        clientHeight -
        heightAnnouncementBar -
        heightHeader -
        heightSearchTop -
        heightInput -
        (buttonHeight || 0) -
        10;

      // If you want to enforce the height cap, uncomment below.
      // document
      //   .querySelectorAll(".predictive-search")
      //   .forEach((el) => (el.style.maxHeight = `${maxHeightAllowed}px`));
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  /* ---------------------------- Query plumbing --------------------------- */
  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Drop previous DOM results if the prefix changed (stale cache view)
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update UI button label immediately
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a') ||
      (event.submitter && event.submitter.classList.contains("card__link"))
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (this.classList.contains("search-modal__form")) {
      if (this.headerBottom) this.headerBottom.classList.add("header__bottom--visible");
      if (this.promoModal) this.promoModal.classList.remove("search__modal--hidden");
    }

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Term was changed in another search input instance
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
      if (this.promoModal && this.classList.contains("search-modal__form")) {
        this.promoModal.classList.add("search__modal--hidden");
      }
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (this.classList.contains("search-modal__form")) {
      if (this.headerBottom) this.headerBottom.classList.remove("header__bottom--visible");
    }
    if (!this.classList.contains("search-modal__form")) {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }
  }

  onBlur() {
    if (this.headerInput) this.headerInput.blur();
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "Enter":
        event.preventDefault();
        this.selectOption();
        break;
      case "Escape":
        this.closeResults(true);
        if (this.promoModal && this.classList.contains("search-modal__form")) {
          this.promoModal.classList.add("search__modal--hidden");
        }
        break;
      default:
        break;
    }

    if (this.getQuery().length) {
      this.hidePromoBlock();
    } else {
      this.showPromoBlock();
    }
  }

  onKeydown(event) {
    // Stop caret jumping on arrow keys if you later re-enable list navigation
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;

    if (currentButtonText) {
      // Avoid over-replacing if the term exists more than once in the button
      if (Array.from(currentButtonText.matchAll(new RegExp(previousTerm, "g"))).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }

    if (this.getQuery().length) this.hidePromoBlock();
    else this.showPromoBlock();
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );
    if (selectedOption) selectedOption.click();
  }

  /* ---------------------------- CORE: fetch ------------------------------- */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    // Build a SKU-aware predictive search URL
const url =
  `${routes.predictive_search_url}?` +
  [
    `q=${encodeURIComponent(searchTerm)}`,
    `section_id=predictive-search`,
    `resources[type]=product,collection,page,article`,
    `resources[limit]=8`,
    // ✅ Only allowed fields
    `resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode,tag,vendor`,
    `resources[options][unavailable_products]=last`
  ].join("&");


    // Serve cached DOM if present
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.clickSearchTabs();
      this.calcHeight();
      this.boostSkuMatches(this.searchTerm);
      return;
    }

    if (this.promoModal && this.classList.contains("search-modal__form")) {
      this.promoModal.classList.add("search__modal--hidden");
    }

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const container = new DOMParser().parseFromString(text, "text/html");
        const section = container.querySelector("#shopify-section-predictive-search");
        const resultsMarkup = section ? section.innerHTML : "";

        // keep caches in sync across instances
        this.allPredictiveSearchInstances.forEach((inst) => {
          inst.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
        this.clickSearchTabs();
        this.calcHeight();
        this.boostSkuMatches(this.searchTerm);

        try { colorSwatches(); } catch (err) {}
      })
      .catch((error) => {
        if (error?.code === 20) return; // fetch aborted
        this.close();
        throw error;
      });
  }

  /* ---------------------------- A11y / Live region ------------------------ */
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
    const txt = document.querySelector(".predictive-search__text");
    if (txt) txt.style.display = "none";
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.setLiveRegionResults();
    this.open();
    if (this.promoModal && this.classList.contains("search-modal__form")) {
      this.promoModal.classList.add("search__modal--hidden");
    }
  }

  clickSearchTabs() {
    const results = this.querySelectorAll(".predictive-search__results-list");
    if (results.length !== 0) {
      results[0].classList.add("active");
      const firstTab = this.querySelector(".predictive-search__result-tab");
      if (firstTab) firstTab.classList.add("active");

      this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
        tab.addEventListener("click", (event) => {
          event.preventDefault();
          const typeTarget = tab.dataset.typeTarget;

          this.querySelectorAll(".predictive-search__result-tab").forEach((el) =>
            el.classList.remove("active")
          );
          tab.classList.add("active");

          results.forEach((el) => {
            el.classList.toggle("active", el.dataset.type === typeTarget);
          });
        });
      });
    }
  }

  hidePromoBlock() {
    const c = (sel) => document.querySelector(sel);
    const a = (sel) => document.querySelectorAll(sel);
    c(".search__categories")?.classList.add("hide");
    c(".search__promo-block")?.classList.add("hide");
    a(".search__line")?.forEach((el) => el.classList.add("hide"));
  }

  showPromoBlock() {
    const c = (sel) => document.querySelector(sel);
    const a = (sel) => document.querySelectorAll(sel);
    c(".search__categories")?.classList.remove("hide");
    c(".search__promo-block")?.classList.remove("hide");
    a(".search__line")?.forEach((el) => el.classList.remove("hide"));
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countEl = this.querySelector("[data-predictive-search-live-region-count-value]");
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    if (this.promoModal && this.classList.contains("search-modal__form")) {
      this.promoModal.classList.remove("search__modal--hidden");
    }

    const hasGroups = !!document.querySelector(".predictive-search__results-groups-wrapper");
    const modal = document.querySelector(".search-modal");
    if (modal) modal.classList.toggle("result_true", hasGroups);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    const modal = document.querySelector(".search-modal");
    if (modal) modal.classList.remove("result_true");
    this.input.setAttribute("aria-expanded", false);
    this.predictiveSearchResults?.removeAttribute("style");
    if (this.promoModal) this.promoModal.classList.add("search__modal--hidden");
  }

  /* ---------------------------- SKU Boosting ------------------------------ */
  /**
   * Move exact SKU matches to the top of the products list.
   * Requires each product result item to have data-sku="..."
   */
  boostSkuMatches(term) {
    if (!term) return;
    const norm = (s) => String(s).replace(/\s+/g, "").toLowerCase();
    const q = norm(term);

    const productsList = this.querySelector(
      '.predictive-search__results-list[data-type="products"]'
    );
    if (!productsList) return;

    const items = Array.from(productsList.querySelectorAll("[data-sku]"));
    if (!items.length) return;

    const exact = items.filter((el) => norm(el.dataset.sku) === q);
    // Prepend exact matches in reverse order to preserve their original order
    for (let i = exact.length - 1; i >= 0; i--) {
      productsList.prepend(exact[i]);
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
